function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useRef, useState, useCallback } from 'react';
import { NodeProps, Position, useConnection } from '@xyflow/react';
import { useShallow } from 'zustand/react/shallow';
import clsx from 'clsx';
import { Editor, EditorEvents } from '@tiptap/react';
import { SvgIconReact } from 'common/components/svg-icon';
import { InputSize, InputType } from 'common/components/input/types';
import { InputReact } from 'common/components/input';
import { KEY_VALUES } from 'common/util/dom';
import { AppState, useCanvasStore } from 'iron/common/report/canvas/store';
import { CustomHandle } from 'common/report/canvas/components/node-handle';
import { AppEdge } from 'common/report/canvas/edges/types';
import { ControlBarReact } from 'common/widgets/control-bar';
import { ButtonTheme } from 'common/components/button/constants';
import { Section } from 'common/components/items-menu/types';
import { Directions } from 'common/components/drop-menu/types';
import { TipTapEditor } from 'common/components/tiptap-editor';
import styles from './index.module.styl';
import { TextNodeType } from './types';
enum ControlBarAction {
  Rename = "rename",
  Duplicate = "duplicate",
  Delete = "delete",
}
function NodeHeader(_ref: {
  title: string;
  isViewOnly: boolean;
  isEditing: boolean;
  onNodeClick: () => void;
  onTitleClick: () => void;
  onTitleUpdate: (updatedTitle: string) => void;
  onControlAction: (action: ControlBarAction) => void;
}) {
  let {
    title,
    isViewOnly,
    isEditing,
    onNodeClick,
    onTitleClick,
    onTitleUpdate,
    onControlAction
  }: {
    title: string;
    isViewOnly: boolean;
    isEditing: boolean;
    onNodeClick: () => void;
    onTitleClick: () => void;
    onTitleUpdate: (updatedTitle: string) => void;
    onControlAction: (action: ControlBarAction) => void;
  } = _ref;
  const overflowControls: Array<Section<ControlBarAction>> = [{
    items: [{
      label: "Rename",
      icon: "text",
      value: ControlBarAction.Rename
    }, {
      label: "Duplicate",
      icon: "duplicate",
      value: ControlBarAction.Duplicate
    }, {
      label: "Delete",
      icon: "trashcan",
      value: ControlBarAction.Delete
    }]
  }];
  return <div className={clsx(styles.nodeHeader, {
    [styles.empty]: !title
  })} onClick={onNodeClick} data-sentry-component="NodeHeader" data-sentry-source-file="index.tsx">
      <SvgIconReact className={styles.genericTextIcon} icon='two-tone' data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
      {isEditing && !isViewOnly ? <InputReact autoFocus type={InputType.Text} size={InputSize.Medium} placeholder='' value={title} onBlur={ev => onTitleUpdate(ev.target.value as string)} onKeydown={ev => {
      if (ev.key === KEY_VALUES.ENTER) {
        ev.preventDefault();
        (ev.target as HTMLInputElement).blur();
      }
    }} /> : <span className={styles.title} onClick={onTitleClick}>
          {title || "New"}
        </span>}
      {!isViewOnly && <div className={styles.controlBar}>
          <ControlBarReact theme={ButtonTheme.Tertiary} overflowIcon={"ellipsis"} overflowControls={overflowControls} overflowMenuWidth={150} overflowMenuAlignment={Directions.Right} onActionSelect={(_ref2: {
        value: ControlBarAction;
      }) => {
        let {
          value
        }: {
          value: ControlBarAction;
        } = _ref2;
        return onControlAction(value);
      }} />
        </div>}
    </div>;
}
function NodeHandles(_ref3: {
  id: string;
  activeEdge: null | AppEdge;
  isSelected: boolean;
}) {
  let {
    id,
    activeEdge,
    isSelected
  }: {
    id: string;
    activeEdge: null | AppEdge;
    isSelected: boolean;
  } = _ref3;
  const connection = useConnection();
  const isTargetNode = connection.inProgress && connection.fromNode.id !== id;
  return <>
      {!connection.inProgress && <>
          {[Position.Top, Position.Bottom, Position.Left, Position.Right].map(position => <CustomHandle key={"".concat(position, "-source")} type={"source"} position={position} isSelected={isSelected} showHandle={isSelected || (activeEdge === null || activeEdge === void 0 ? void 0 : activeEdge.source) === id && (activeEdge === null || activeEdge === void 0 ? void 0 : activeEdge.sourceHandle) === "".concat(position, "-source")} />)}
        </>}
      {(!connection.inProgress || isTargetNode) && <>
          {[Position.Top, Position.Bottom, Position.Left, Position.Right].map(position => <CustomHandle key={"".concat(position, "-target")} type={"target"} position={position} isSelected={isSelected} showHandle={!isSelected && isTargetNode || (activeEdge === null || activeEdge === void 0 ? void 0 : activeEdge.target) === id && (activeEdge === null || activeEdge === void 0 ? void 0 : activeEdge.targetHandle) === "".concat(position, "-target")} />)}
        </>}
    </>;
}
const selector = (state: AppState) => {
  var _state$canvas, _state$ghostNode;
  return {
    isViewOnly: !((_state$canvas = state.canvas) !== null && _state$canvas !== void 0 && _state$canvas.can_update_basic),
    activeEdge: state.activeEdge,
    addNode: state.addNode,
    addEdge: state.addEdge,
    deleteNode: state.deleteNode,
    edges: state.edges,
    generateId: state.generateId,
    ghostNodeId: (_state$ghostNode = state.ghostNode) === null || _state$ghostNode === void 0 ? void 0 : _state$ghostNode.id,
    setActiveMetricNodeId: state.setActiveMetricNodeId,
    setTextNodeTitle: state.setTextNodeTitle,
    setTextNodeContent: state.setTextNodeContent
  };
};
export function TextNode(_ref4: NodeProps<TextNodeType>) {
  let {
    id,
    data,
    selected,
    dragging,
    positionAbsoluteX,
    positionAbsoluteY,
    height = 0
  }: NodeProps<TextNodeType> = _ref4;
  const {
    isViewOnly,
    activeEdge,
    addNode,
    addEdge,
    deleteNode,
    edges,
    generateId,
    ghostNodeId,
    setActiveMetricNodeId,
    setTextNodeTitle,
    setTextNodeContent
  } = useCanvasStore(useShallow(selector));

  // local state, no need to sync to store
  const [isEditingNodeName, setEditingNodeName] = useState<boolean>(false);
  const ref = useRef<Editor | null>(null);
  const [focused, setFocused] = useState(false);
  const handleContentFocus = useCallback<(editorProps: EditorEvents[`focus`]) => void>(_ref5 => {
    let {
      editor
    } = _ref5;
    setFocused(editor.isFocused);
  }, [setFocused]);
  const handleContentBlur = useCallback<(editorProps: EditorEvents[`blur`]) => void>(_ref6 => {
    let {
      editor
    } = _ref6;
    setFocused(editor.isFocused);
  }, [setFocused]);
  const handleNodeClick = () => {
    // close qb panel on node click
    setActiveMetricNodeId(null);
    // next click will focus the editor
    if (selected && !focused) {
      var _ref$current;
      (_ref$current = ref.current) === null || _ref$current === void 0 || _ref$current.chain().focus("end").run();
    }
  };
  const handleTitleClick = () => setEditingNodeName(true);
  const handleTitleUpdate = (newTitle: string) => {
    setEditingNodeName(false);
    setTextNodeTitle(id, newTitle);
  };
  const handleContentUpdate = useCallback<(editorProps: EditorEvents[`update`]) => void>(_ref7 => {
    let {
      editor
    } = _ref7;
    setTextNodeContent(id, editor.getHTML());
  }, [setTextNodeContent, id]);
  const handleControlAction = (action: ControlBarAction) => {
    switch (action) {
      case ControlBarAction.Rename:
        setEditingNodeName(true);
        break;
      case ControlBarAction.Duplicate:
        {
          // Create the duplicated node (apply offset position to avoid overlap)
          const newNodeId = generateId("node-");
          const newNode: TextNodeType = {
            id: newNodeId,
            type: "text",
            position: {
              x: positionAbsoluteX,
              y: positionAbsoluteY + height + 3
            },
            data: {
              title: data.title,
              markdown: data.markdown
            }
          };

          // Find connected edges
          const connectedEdges = edges.filter(edge => edge.source === id || edge.target === id);

          // Create the duplicated edges
          const newEdges = connectedEdges.map(edge => _objectSpread(_objectSpread({}, edge), {}, {
            id: generateId("edge"),
            source: edge.source === id ? newNodeId : edge.source,
            target: edge.target === id ? newNodeId : edge.target
          }));

          // Update nodes and edges
          addNode(newNode);
          newEdges.forEach(edge => addEdge(edge));
          break;
        }
      case ControlBarAction.Delete:
        deleteNode(id);
        break;
    }
  };
  const isGhostNode = id === ghostNodeId;
  return <div tabIndex={0} key={id} className={clsx(styles.textNode, {
    [styles.ghost]: isGhostNode
  })} data-sentry-component="TextNode" data-sentry-source-file="index.tsx">
      {!isGhostNode && <NodeHandles id={id} activeEdge={activeEdge} isSelected={selected} />}
      <NodeHeader title={data.title} isViewOnly={isViewOnly} isEditing={isEditingNodeName} onNodeClick={handleNodeClick} onTitleClick={handleTitleClick} onTitleUpdate={handleTitleUpdate} onControlAction={handleControlAction} data-sentry-element="NodeHeader" data-sentry-source-file="index.tsx" />
      <div className={styles.nodeBody} onClick={handleNodeClick}>
        <TipTapEditor ref={ref} className={clsx({
        [styles.textEditor]: true,
        [styles.notSelected]: !selected || dragging,
        [styles.notFocused]: !focused
      })} onFocus={handleContentFocus} onBlur={handleContentBlur} canEdit={!isViewOnly} alwaysShowPlaceHolder placeholder='Write about your data...' autofocus={!data.markdown && !isGhostNode} content={data.markdown} onUpdate={handleContentUpdate} data-sentry-element="TipTapEditor" data-sentry-source-file="index.tsx" />
      </div>
    </div>;
}